<script setup lang="ts">
import { deleteFavouriteLocation } from '@/api/calls/directus-calls/favourite-locations-calls'
import AlertPopupWindow from '@/components/alert-popup/AlertPopupWindow.vue'
import { type State, MutationTypes } from '@/store/store_types'
import { getNiceDuration } from '@/utils/timeUtils'
import { computed, ref, toRaw } from 'vue'
import { useStore } from 'vuex'
import FavLocationForm from '../favourite-locations/FavLocationForm.vue'

const store = useStore<State>()

const locationData = computed(() => store.state.selectedFavLocation)

const can_edit_location = computed<boolean>(() => {
  // case fleet location
  if (locationData.value && locationData.value.visibility === 'fleet') {
    return store.state.permissions.hasFieldPermission(
      'Favourite_Locations',
      'update',
      'approval_status',
    )
  }
  // case personal location
  if (locationData.value && locationData.value.visibility === 'private') {
    return locationData.value.user_created_directus_id === store.state.user?.directusId
  }
  // default no access
  return false
})

const show_edit_form = ref(false)

const show_delete_confirmation = ref(false)
const async_delete_in_progress = ref(false)
const delete_outcome = ref<'ok' | 'failed' | null>(null)
const handle_delete = async () => {
  async_delete_in_progress.value = true
  if (locationData.value?.directusId) { 
    delete_outcome.value = await deleteFavouriteLocation(locationData.value?.directusId)
  } else {
    delete_outcome.value = 'failed'
  }
  async_delete_in_progress.value = false
}

const delete_message = computed(() => {
  if (async_delete_in_progress.value) return 'Processing...'
  if (delete_outcome.value === 'ok') return 'Location successfully deleted.'
  if (delete_outcome.value === 'failed')
    return 'An error occurred while trying to delete this location.'
  return 'This can not be undone, are you sure you want to delete this location.'
})

const delete_show_actions = computed(() => {
  if (async_delete_in_progress.value) return false
  if (delete_outcome.value) return false
  return true
})

const clean_up_delete = () => {
  if (locationData.value && delete_outcome.value === 'ok') {
    const snapshot = { ...toRaw(locationData.value) }
    // remove locally
    store.commit(MutationTypes.deleteFavLocation, snapshot.localId)
    // navigate to list view
    store.commit(
      MutationTypes.setDashboardDialogContent,
      snapshot.visibility === 'fleet' ? 'fleet-locations' : 'saved-location-list',
    )
  }
  // clean up local state
  show_delete_confirmation.value = false
  async_delete_in_progress.value = false
  delete_outcome.value = null
}

const niceDuration = (seconds: number): string => getNiceDuration(seconds)
</script>

<style scoped>
.hover-black:hover {
  color: rgb(var(--v-theme-pwt_dark_grey)) !important;
}
.hover-red:hover {
  color: rgb(var(--v-theme-pwt_red)) !important;
}
</style>

<template>
  <v-container v-if="locationData" class="px-5 bg-pwt_snow">
    <!-- editable content -->
    <v-card v-if="show_edit_form" class="pa-5" color="transparent" flat>
      <FavLocationForm :initial-value="locationData" @close="() => (show_edit_form = false)" />
    </v-card>

    <!-- readonly content -->
    <v-card v-else flat class="bg-pwt_snow">
      <v-card-title class="d-flex flex-nowrap justify-space-between">
        {{ locationData.name }}
        <div v-if="can_edit_location">
          <v-btn
            icon
            flat
            color="transparent"
            density="compact"
            class="mr-1"
            @click.stop="
              () => {
                show_edit_form = true
              }
            "
          >
            <v-icon icon="mdi-pencil" color="pwt_light_grey" class="hover-black" />
          </v-btn>
          <v-btn
            icon
            flat
            color="transparent"
            density="compact"
            @click.stop="
              () => {
                show_delete_confirmation = true
              }
            "
          >
            <v-icon icon="mdi-trash-can-outline" color="pwt_light_grey" class="hover-red" />
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text> {{ locationData.address }} </v-card-text>

      <v-card-text>
        <v-card class="mb-5 pa-4" rounded="lg" flat>
          <ul style="list-style: none; padding-left: unset">
            <li class="text-h6">Location privacy settings</li>
            <li>visibility: {{ locationData.visibility }}</li>
            <li>location type: {{ locationData.type }}</li>
          </ul>
        </v-card>
        <v-card class="mb-5 pa-4" rounded="lg" flat>
          <ul
            v-if="locationData.planningData"
            style="list-style: none; padding-left: unset"
            class="pb-5"
          >
            <li class="text-h6">Trip Planning Defaults</li>
            <li v-if="locationData.planningData.loadWeightChange">
              load weight change: {{ locationData.planningData.loadWeightChange }} kg
            </li>
            <li
              v-if="
                locationData.planningData.chargeHere &&
                locationData.planningData.rating &&
                locationData.planningData.currentType
              "
            >
              Charge here with {{ locationData.planningData.rating }}kW
              {{ locationData.planningData.currentType }} charger
            </li>
            <li v-if="locationData.planningData.chargeHere">
              Normal state of charge after charging at this location:
              {{ locationData.planningData.SOCAfterCharging ?? 0 }}%
            </li>
            <li v-if="locationData.planningData.energyUsed">
              use {{ locationData.planningData.energyUsed }}kWh energy for idle processes at this
              location
            </li>
            <li v-if="locationData.planningData.stayDuration">
              Normal expected stay duration at this location:
              {{ niceDuration(locationData.planningData.stayDuration) }}
            </li>
          </ul>
          <template v-else>
            <v-card-title class="px-0">Trip Planning Defaults</v-card-title>
            <v-card-text class="px-0">
              No trip planning defaults have been set for this location
            </v-card-text>
          </template>
        </v-card>
      </v-card-text>
    </v-card>
  </v-container>
  <v-container v-else class="px-5 bg-pwt_snow">
    <v-card flat class="bg-pwt_snow">
      <v-card-title class="text-center"> Location not found </v-card-title>
      <v-card-text class="text-center"> The location you selected could not be found. </v-card-text>
    </v-card>
  </v-container>
  <AlertPopupWindow
    :activator="show_delete_confirmation"
    :title="'Delete ' + (locationData?.address ?? 'this location')"
    :message="delete_message"
    @close="() => (show_delete_confirmation = false)"
    :display-do-not-show-again-input="false"
    :show-loading-animation="async_delete_in_progress"
    :show-actions="delete_show_actions"
    @continue="handle_delete"
    @after-leave="clean_up_delete"
  />
</template>
